import { useQuery, useMutation, useQueryClient } from 'react-query';
import studentService from '../services/api/student';
import { 
  getVideoUrl, 
  getVideoThumbnail, 
  getLessonDuration, 
  getLessonViews,
  getVideoType,
  isExternalVideo
} from '../utils/videoUtils';
import { getServerUrl } from '../config/apiConfig';

const SERVER_BASE_URL = getServerUrl();

/**
 * Hook لجلب بيانات الدرس
 */
export const useLessonData = (lessonId) => {
  return useQuery(
    ['lesson', lessonId],
    async () => {
      try {
        const response = await studentService.getLesson(lessonId);
        
        const lessonData = response?.data?.lesson || response?.data;
        const courseData = response?.data?.course || {};
        const ratingsData = response?.data?.ratings || null;
        const navigationData = response?.data?.navigation || { previous: null, next: null };

        // جلب بيانات الكورس كاملة إذا كان موجودًا
        let fullCourseData = courseData;
        if (courseData.id && !courseData.title) {
          try {
            const courseResponse = await studentService.getCourse(courseData.id);
            fullCourseData = { ...courseData, ...courseResponse?.data?.data };
          } catch (err) {
            // Silently handle course data fetch failure
          }
        }

        // تحسين بيانات الدرس باستخدام videoUtils
        const enhancedLesson = {
          ...lessonData,
          // Ratings: average across users and the current user's rating if present
          average_rating: ratingsData?.average ? Number(ratingsData.average) : (lessonData.average_rating || lessonData.rating || lessonData.avg_rating || null),
          user_rating: lessonData.user_rating ?? lessonData.userRating ?? null,
          ratings_total: ratingsData?.total ?? lessonData.ratings_total ?? null,
          recent_ratings: ratingsData?.recent || lessonData.recent_ratings || [],
          video_url: getVideoUrl(lessonData),
          thumbnail_url: getVideoThumbnail(lessonData.video_url, lessonData),
          duration_seconds: getLessonDuration(lessonData),
          duration: lessonData.duration,
          views: getLessonViews(lessonData),
          video_type: getVideoType(lessonData.video_url),
          is_external: isExternalVideo(lessonData.video_url),
          teacher_name: fullCourseData.teacher_name || lessonData.teacher_name,
          course_title: fullCourseData.title || fullCourseData.subject_name || lessonData.course_title,
          teacher_subject_id: fullCourseData.teacher_subject_id || lessonData.teacher_subject_id,
          course_id: fullCourseData.id || lessonData.course_id,
          file_path: lessonData.file_path?.startsWith('http')
            ? lessonData.file_path
            : lessonData.file_path
            ? `${SERVER_BASE_URL}${lessonData.file_path}`
            : null,
          // إنشء progress object من البيانات المُرجعة من الخادم
          progress: {
            completed: lessonData.completed || false,
            progress: lessonData.progress || 0,
            lastWatchedPosition: lessonData.last_watched_position || 0,
            completed_at: lessonData.completed_at,
            last_watched_at: lessonData.last_watched_at
          },
          // تمرير بيانات الملاحة (الدرس السابق والتالي)
          navigation: navigationData
        };

        return {
          ...response,
          data: {
            ...response.data,
            lesson: enhancedLesson
          }
        };
      } catch (error) {
        // Error fetching lesson data
        throw error;
      }
    },
    {
      enabled: !!lessonId,
      retry: 2,
      retryDelay: 1000,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

/**
 * Hook لجلب أسئلة الدرس
 */
export const useLessonQuestions = (lessonId) => {
  return useQuery(
    ['questions', lessonId],
    () => studentService.getLessonQuestions(lessonId),
    {
      refetchInterval: 30000,
      enabled: !!lessonId,
      retry: (failureCount, error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          return false;
        }
        return failureCount < 2;
      },
    }
  );
};

/**
 * Hook لتحديث تقدم المشاهدة (Progress Tracking)
 */
export const useUpdateProgress = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ lessonId, progressData }) => {
      return studentService.updateLessonProgress(lessonId, progressData)
        .then(response => {
          return response;
        })
        .catch(error => {
          throw error;
        });
    },
    {
      onSuccess: (data, { lessonId }) => {
        // تحديث بيانات الدرس المخزنة محلياً
        queryClient.invalidateQueries(['lesson', lessonId]);
        // تحديث قائمة دروس الطالب فوراً بعد تحديث التقدم
        queryClient.invalidateQueries('student-lessons');
        // إعادة جلب البيانات الجديدة مباشرة
        queryClient.refetchQueries(['lesson', lessonId]);
      },
      onError: (error, { lessonId }) => {
        // Progress update error handling
      },
    }
  );
};

/**
 * Hook لجلب إحصائيات الفيديو من Bunny Stream (بيانات دقيقة)
 * للمعلمين والإدارة فقط
 */
export const useLessonAnalytics = (lessonId, userRole) => {
  // فقط المعلمون والإدارة يرون الإحصائيات
  const canViewAnalytics = userRole === 'teacher' || userRole === 'admin';

  return useQuery(
    ['lesson-analytics', lessonId],
    () => studentService.getLessonAnalytics(lessonId),
    {
      enabled: !!lessonId && canViewAnalytics, // لا تطلب البيانات إذا كان الطالب
      retry: false, // لا تحاول مرة أخرى عند الفشل
      staleTime: 10 * 60 * 1000, // 10 minutes
      refetchInterval: canViewAnalytics ? 30000 : false, // تحديث فقط للمعلمين
      onError: (error) => {
        if (error.response?.status !== 403) {
          // Analytics fetch error handling
        }
      },
    }
  );
};

/**
 * Hook لجلب تعليقات الدرس
 */
export const useLessonComments = (lessonId) => {
  return useQuery(
    ['comments', lessonId],
    () => studentService.getLessonComments(lessonId),
    {
      refetchInterval: 45000,
      enabled: !!lessonId,
      retry: 2,
    }
  );
};

/**
 * Hook لإضافة سؤال
 */
export const useAddQuestion = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    ({ lessonId, questionText }) => 
      studentService.askQuestion(lessonId, { text: questionText }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['questions', variables.lessonId]);
      },
    }
  );
};

/**
 * Hook لإضافة تعليق - غير متوفر حالياً في API
 */
export const useAddComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    ({ lessonId, commentText, parentId, tags }) => {
      // TODO: Implement when API supports comments
      return Promise.resolve();
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['comments', variables.lessonId]);
      },
    }
  );
};

/**
 * Hook للتصويت على سؤال - غير متوفر حالياً في API
 */
export const useVoteQuestion = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    ({ questionId, voteType }) => {
      // TODO: Implement when API supports voting
      return Promise.resolve();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['questions']);
      },
    }
  );
};

/**
 * Hook لتفضيل سؤال - غير متوفر حالياً في API
 */
export const useFavoriteQuestion = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (questionId) => {
      // TODO: Implement when API supports favoriting
      return Promise.resolve();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['questions']);
      },
    }
  );
};

/**
 * Hook لتمييز الدرس كمكتمل
 */
export const useMarkLessonComplete = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    (lessonId) => studentService.updateLessonProgress(lessonId, { completed: true }),
    {
      onSuccess: (_, lessonId) => {
        queryClient.invalidateQueries(['lesson', lessonId]);
        queryClient.invalidateQueries(['courses']);
      },
      onError: (error) => {
        // Mark lesson complete error handling
      },
    }
  );
};
